import { IPermissionProps } from '@/types/shared'
import React, { useEffect, useMemo } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { Alert, Button, IconButton, Typogaphy } from 'vnetwork-ui'
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react'
import { DayOffNewPayload, DayOffType } from '../../types/payload'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import useAuthStore from '@/stores/useAuthStore'
import dayjs from 'dayjs'
import toast from 'react-hot-toast'
import AddIcons from '@/icons/AddIcons'
import { createDayOff, updateDayOff } from '../../services'
import { useQueryClient } from '@tanstack/react-query'
import { DayOffResponse } from '../../types/response'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useTourHandler } from '@/contexts/tour-handler'
import { useDayOffTour } from '../../hooks/use-dayoff-tour'
import SubHeaders from '@/components/Headers/SubHeaders'
import { paths } from '@/utils/path'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'
import RangeTimeItem from './RangeTimeItem'
import { FormItem } from 'vnetwork-ui'
import { dayOffSchema } from './yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import Statistical from './Statistical'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useOncallConflictCheck } from '../../hooks/use-oncall-conflict-check'
import { CalendarClock } from 'lucide-react'

interface DayOffFormProps extends IPermissionProps {
    pageType: 'create' | 'update'
    dayOff?: DayOffResponse | null
    refetchData?: () => void
}

const DayOffCreate: React.FC<DayOffFormProps> = ({
    pageType = 'create',
    dayOff = {} as DayOffResponse,
    refetchData = () => {},
}) => {
    const { t: translate } = useTranslation()

    // Tour for DayOff Create form
    const { startTour } = useDayOffTour()
    const { registerHandler, unregisterHandler } = useTourHandler()

    // Register tour handler when component mounts
    useEffect(() => {
        registerHandler(startTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startTour])
    const { employee } = useAuthStore()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const employeeRefetch = useDataGridRefetch({
        reactQueryKey: 'verify-token',
    })

    const refetchAllDayOffQueries = () => {
        const queries = queryClient
            .getQueryCache()
            .getAll()
            .filter((q) => {
                const key = q.queryKey?.[0]
                return typeof key === 'string' && key.startsWith('dayoffs-')
            })

        queries.forEach((q) => q.fetch())
    }
    const [rangeTimeRendered, setRangeTimeRendered] = React.useState(false)
    const [showPolicyDetails, setShowPolicyDetails] = React.useState(false)
    const location = useLocation()
    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { isValid },
    } = useForm<DayOffNewPayload>({
        mode: 'onChange',
        defaultValues: {
            day_off: {
                day_off_range_time:
                    pageType === 'update' && dayOff
                        ? dayOff?.day_off_range_time?.map((item) => {
                              return {
                                  start_day_off: dayjs(
                                      item?.start_day_off
                                  ).format('YYYY-MM-DD'),
                                  day_off_option: item?.day_off_option,
                              }
                          })
                        : [
                              {
                                  start_day_off: location.state?.startDate
                                      ? dayjs(location.state.startDate).format(
                                            'YYYY-MM-DD'
                                        )
                                      : dayjs(new Date()).format('YYYY-MM-DD'),
                                  day_off_option: 0,
                              },
                          ],
                day_off_reason:
                    pageType === 'update' && dayOff
                        ? dayOff?.day_off_reason
                        : '',
                day_off_type:
                    pageType === 'update' && dayOff
                        ? dayOff?.day_off_type
                        : DayOffType.normal,
            },
        },
        //@ts-ignore
        resolver: yupResolver(dayOffSchema(translate)),
    })

    const { append, remove } = useFieldArray({
        control,
        name: 'day_off.day_off_range_time',
    })

    useEffect(() => {
        if (dayOff && pageType === 'update') {
            setValue(
                'day_off.day_off_range_time',
                dayOff?.day_off_range_time?.map((item) => {
                    return {
                        start_day_off: dayjs(item?.start_day_off).format(
                            'YYYY-MM-DD'
                        ),
                        day_off_option: item?.day_off_option,
                    }
                })
            )
            setValue('day_off.day_off_reason', dayOff?.day_off_reason)
            setValue('day_off.day_off_type', dayOff?.day_off_type)
        }
    }, [dayOff])

    const { mutateAsync: create, isPending: isCreating } =
        useMutationWithHandler(
            {
                mutationFn: createDayOff,
            },
            {
                reactQueryKeys: QUERY_KEYS.DAYOFFS,
                errorMessage: 'something went wrong',
            }
        )

    const { mutateAsync: update, isPending: isUpdating } =
        useMutationWithHandler(
            {
                mutationFn: updateDayOff,
            },
            {
                reactQueryKeys: QUERY_KEYS.DAYOFFS,
                errorMessage: 'something went wrong',
            }
        )
    const onSubmit: any = async (data: DayOffNewPayload) => {
        console.log(refetchData)
        if (data?.day_off?.day_off_reason?.trim() === '') {
            return toast.error('Vui lòng nhập lý do')
        }
        if (pageType === 'create') {
            try {
                await create(data)
                if (data?.day_off?.day_off_type === 'annual_leave') {
                    employeeRefetch.refetchCurrent()
                }
                toast.success('Create day off success')
                if (refetchData) refetchData()
                refetchAllDayOffQueries()
                queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.DAY_OFF_SUMMARY],
                })
                navigate('/account/dayoffs')
            } catch (error) {
                console.log(error)
            }
        } else {
            try {
                await update({
                    id: Number(dayOff?.day_off_id),
                    dayOff: {
                        day_off: {
                            day_off_range_time:
                                data?.day_off?.day_off_range_time,
                            day_off_reason: data.day_off.day_off_reason,
                        },
                    },
                })
                if (data?.day_off?.day_off_type === 'annual_leave') {
                    employeeRefetch.refetchCurrent()
                }
                toast.success('Update day off success')
                if (refetchData) refetchData()
                refetchAllDayOffQueries()
                queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.DAY_OFF_SUMMARY],
                })
                navigate('/account/dayoffs')
            } catch (error) {
                console.log(error)
            }
        }
    }

    const addRangeTime = () => {
        if (
            watch('day_off.day_off_type') === 'annual_leave' &&
            calDayOffLeftEmployee() - 0.5 < 0
        ) {
            toast.error('No day off left')
            return
        }
        append({
            start_day_off: dayjs(
                watch('day_off.day_off_range_time')[
                    watch('day_off.day_off_range_time')?.length - 1
                ]?.start_day_off
            )
                .add(1, 'day')
                .format('YYYY-MM-DD'),
            day_off_option: 0,
        })
    }

    const removeRangeTime = (index: number) => {
        remove(index)
    }

    const calDayOffLeftEmployee = (): number => {
        return (
            Number(employee?.employee_day_off_left) -
            (watch('day_off.day_off_range_time')
                .map((item) => {
                    switch (item?.day_off_option) {
                        case 0:
                        case 1:
                            return 0.5
                        case 2:
                            return 1
                        default:
                            break
                    }
                })
                .reduce((a, b) => a + (b || 0), 0) -
                (dayOff?.day_off_id
                    ? dayOff?.day_off_range_time
                          ?.map((item) => {
                              switch (item?.day_off_option) {
                                  case 0:
                                  case 1:
                                      return 0.5
                                  case 2:
                                      return 1
                                  default:
                                      break
                              }
                          })
                          .reduce((a, b) => a + (b || 0), 0)
                    : 0))
        )
    }

    // Policy enforcement date: 01/01/2026
    const POLICY_START_DATE = dayjs('2026-01-01')
    const isPolicyActive =
        dayjs().isAfter(POLICY_START_DATE) ||
        dayjs().isSame(POLICY_START_DATE, 'day')

    // Min date for date picker: 3 days before today (only when policy is active and on create)
    const minDate =
        isPolicyActive && pageType === 'create'
            ? dayjs().subtract(3, 'day').startOf('day')
            : undefined

    // Validate if earliest date is within 3 days from today
    const rangeTime = watch('day_off.day_off_range_time')
    const dateValidation = useMemo(() => {
        if (!isPolicyActive || pageType === 'update')
            return { isValid: true, error: null }

        const today = dayjs().startOf('day')
        const entries = rangeTime || []

        if (entries.length === 0) return { isValid: true, error: null }

        const earliestEntry = entries.reduce((earliest, entry) => {
            const entryDate = dayjs(entry.start_day_off)
            return entryDate.isBefore(earliest) ? entryDate : earliest
        }, dayjs(entries[0]?.start_day_off))

        const daysDiff = today.diff(earliestEntry.startOf('day'), 'day')

        // If earliest date is more than 3 days before today, invalid
        if (daysDiff > 3) {
            return {
                isValid: false,
                error: translate('dayoffs:policy_validation_error'),
            }
        }

        return { isValid: true, error: null }
    }, [rangeTime, isPolicyActive, pageType, translate])

    // Leave reservation validation - only for annual_leave type
    const dayOffType = watch('day_off.day_off_type')
    const leaveReservationValidation = useMemo(() => {
        // Only validate for annual_leave and on create
        if (dayOffType !== 'annual_leave' || pageType === 'update') {
            return { isValid: true, error: null, info: null }
        }

        const entries = rangeTime || []
        if (entries.length === 0) {
            return { isValid: true, error: null, info: null }
        }

        // Calculate days per option
        const getDaysForOption = (option: number): number => {
            switch (option) {
                case 0: // morning
                case 1: // afternoon
                    return 0.5
                case 2: // full day
                    return 1
                default:
                    return 0
            }
        }

        // Find earliest month in the request
        const earliestDate = entries.reduce((earliest, entry) => {
            const entryDate = dayjs(entry.start_day_off)
            return entryDate.isBefore(earliest) ? entryDate : earliest
        }, dayjs(entries[0]?.start_day_off))

        const earliestMonth = earliestDate.month() + 1 // 1-12

        // Calculate remaining months after earliest month (e.g., if earliest is June=6, remaining is 7-12)
        const remainingMonths = 12 - earliestMonth // e.g., 12 - 6 = 6 months (Jul-Dec)

        if (remainingMonths <= 0) {
            // December, no reservation needed
            return { isValid: true, error: null, info: null }
        }

        // Count leave days that fall into future months (after earliestMonth)
        let daysInFutureMonths = 0
        entries.forEach((entry) => {
            const entryMonth = dayjs(entry.start_day_off).month() + 1 // 1-12
            if (entryMonth > earliestMonth) {
                daysInFutureMonths += getDaysForOption(entry.day_off_option)
            }
        })

        // Required reservation = remaining months - days already covering future months
        const requiredReservation = Math.max(
            0,
            remainingMonths - daysInFutureMonths
        )

        // Calculate total days in this request
        const totalRequestDays = entries.reduce(
            (sum, entry) => sum + getDaysForOption(entry.day_off_option),
            0
        )

        // Calculate remaining balance after this request
        // Need to account for days already used in update mode
        const existingDays = dayOff?.day_off_id
            ? (dayOff?.day_off_range_time || []).reduce(
                  (sum, item) => sum + getDaysForOption(item.day_off_option),
                  0
              )
            : 0

        const currentBalance = Number(employee?.employee_day_off_left) || 0
        const balanceAfterRequest =
            currentBalance - totalRequestDays + existingDays

        // Generate month names for display
        const monthNames = []
        for (let m = earliestMonth + 1; m <= 12; m++) {
            monthNames.push(m)
        }
        const monthsDisplay = monthNames.join(', ')

        // Check if balance after request >= required reservation
        if (balanceAfterRequest < requiredReservation) {
            return {
                isValid: false,
                error: translate('dayoffs:leave_reservation_error', {
                    required: requiredReservation,
                    months: monthsDisplay,
                    remaining: balanceAfterRequest,
                }),
                info: null,
            }
        }

        // Return info about reservation requirement
        return {
            isValid: true,
            error: null,
            info:
                requiredReservation > 0
                    ? translate('dayoffs:leave_reservation_info', {
                          required: requiredReservation,
                          months: monthsDisplay,
                      })
                    : null,
        }
    }, [rangeTime, dayOffType, pageType, employee, dayOff, translate])

    // Check oncall assignment conflicts
    const {
        hasConflict: hasOncallConflict,
        conflictCount,
        conflictDatesFormatted,
        isChecking: isCheckingOncall,
    } = useOncallConflictCheck({
        employeeId: employee?.employee_id,
        rangeTime: rangeTime || [],
    })

    return (
        <div className="w-full">
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.dayOffAccountList,
                        title: translate('day_off_list'),
                    },
                    {
                        path:
                            pageType === 'create'
                                ? paths.dayOffCreatePage
                                : paths.dayOffEdit.replace(
                                      ':id',
                                      dayOff?.day_off_id?.toString() || ''
                                  ),
                        title:
                            pageType === 'create'
                                ? translate('day_off_create')
                                : `${translate(`day_off_update`)} #${dayOff?.day_off_id?.toString() || ''}`,
                    },
                ]}
            />

            <div>
                <Statistical />

                {/* Policy Notice - only show on create */}
                {pageType === 'create' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-2">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <Typogaphy className="font-semibold text-amber-800">
                                    {translate('dayoffs:policy_notice_title')}
                                </Typogaphy>
                                <Typogaphy
                                    size="small"
                                    className="text-amber-700 mt-1"
                                >
                                    {translate('dayoffs:policy_notice_content')}
                                </Typogaphy>
                                <button
                                    type="button"
                                    onClick={() =>
                                        setShowPolicyDetails(
                                            (current) => !current
                                        )
                                    }
                                    aria-expanded={showPolicyDetails}
                                    className="inline-flex items-center gap-1 mt-1 text-sm text-amber-800 font-semibold underline underline-offset-2 hover:text-amber-900"
                                >
                                    {translate(
                                        'dayoffs:policy_notice_details_toggle'
                                    )}
                                    {showPolicyDetails ? (
                                        <ChevronUp className="w-3 h-3" />
                                    ) : (
                                        <ChevronDown className="w-3 h-3" />
                                    )}
                                </button>
                                {showPolicyDetails && (
                                    <div className="mt-3 rounded-md bg-amber-100/70 px-3 py-2">
                                        <Typogaphy
                                            size="small"
                                            className="whitespace-pre-line text-amber-800"
                                        >
                                            {translate(
                                                'dayoffs:policy_notice_details'
                                            )}
                                        </Typogaphy>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Validation Error */}
                {!dateValidation.isValid && (
                    <Alert variant="error" className="mt-2">
                        {dateValidation.error}
                    </Alert>
                )}

                {/* Leave Reservation Notice - only for annual_leave */}
                {dayOffType === 'annual_leave' && pageType === 'create' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-2">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <Typogaphy className="font-semibold text-blue-800">
                                    {translate(
                                        'dayoffs:leave_reservation_title'
                                    )}
                                </Typogaphy>
                                <Typogaphy
                                    size="small"
                                    className="text-blue-700 mt-1"
                                >
                                    {translate(
                                        'dayoffs:leave_reservation_content'
                                    )}
                                </Typogaphy>
                                {leaveReservationValidation.info && (
                                    <Typogaphy
                                        size="small"
                                        className="text-blue-800 font-medium mt-2"
                                    >
                                        {leaveReservationValidation.info}
                                    </Typogaphy>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Leave Reservation Error */}
                {!leaveReservationValidation.isValid && (
                    <Alert variant="error" className="mt-2">
                        {leaveReservationValidation.error}
                    </Alert>
                )}

                {/* Oncall Conflict Check */}
                {isCheckingOncall && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mt-2 flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                        <Typogaphy size="small" className="text-gray-500">
                            {translate('dayoffs:oncall_conflict_checking')}
                        </Typogaphy>
                    </div>
                )}
                {hasOncallConflict && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mt-2">
                        <div className="flex items-start gap-3">
                            <CalendarClock className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <Typogaphy className="font-semibold text-orange-800">
                                    {translate('dayoffs:oncall_conflict_title')}
                                </Typogaphy>
                                <Typogaphy
                                    size="small"
                                    className="text-orange-700 mt-1"
                                >
                                    {translate(
                                        'dayoffs:oncall_conflict_warning',
                                        {
                                            count: conflictCount,
                                            dates: conflictDatesFormatted.join(
                                                ', '
                                            ),
                                        }
                                    )}
                                </Typogaphy>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex flex-col bg-white rounded-lg mt-2">
                    <div className="bg-white p-4 md:p-4 md:pt-4 md:pb-6 rounded-lg lg:mt-2">
                        <Typogaphy size="medium" className="text-primary">
                            {translate('dayoff-detail')}
                        </Typogaphy>
                        <div className="grid grid-cols-1 lg:grid-cols-12 mt-4">
                            <div className="col-span-4 space-y-4">
                                <div className="grid grid-cols-4 items-start">
                                    <Typogaphy
                                        size="small"
                                        className="col-span-2 "
                                    >
                                        {translate('dayoff-employee-name')}
                                    </Typogaphy>
                                    <Typogaphy
                                        size="small"
                                        className="col-span-2 font-bold"
                                    >
                                        {employee?.employee_name}
                                    </Typogaphy>
                                </div>

                                <div className="grid grid-cols-4 items-start">
                                    <Typogaphy
                                        size="small"
                                        className="col-span-2 "
                                    >
                                        {translate('dayoff-position')}
                                    </Typogaphy>
                                    <Typogaphy
                                        size="small"
                                        className="col-span-2 font-bold"
                                    >
                                        {employee?.position_name}
                                    </Typogaphy>
                                </div>

                                <div className="grid grid-cols-4 items-start">
                                    <Typogaphy
                                        size="small"
                                        className="col-span-2 "
                                    >
                                        {translate('dayoff-department')}
                                    </Typogaphy>
                                    <Typogaphy
                                        size="small"
                                        className="col-span-2 font-bold"
                                    >
                                        {employee?.department_name}
                                    </Typogaphy>
                                </div>
                            </div>

                            <div className="col-span-8 space-y-4">
                                <div className="grid lg:grid-cols-4 grid-cols-1 gap-2 lg:mt-0 mt-2">
                                    <Typogaphy
                                        size="small"
                                        className="col-span-1"
                                    >
                                        {translate('dayoff-type-of-leave')}
                                        <span className="text-red-500">*</span>
                                    </Typogaphy>
                                    <div className="col-span-3">
                                        <div
                                            id="day-off-type"
                                            className="mt-[-8px] w-fit"
                                        >
                                            <FormItem
                                                disabled={pageType === 'update'}
                                                type="radiogroup"
                                                direction="row"
                                                control={control}
                                                name="day_off.day_off_type"
                                                options={[
                                                    {
                                                        label: translate(
                                                            'normal'
                                                        ),
                                                        value: 'normal',
                                                    },
                                                    {
                                                        label: translate(
                                                            'annual_leave'
                                                        ),
                                                        value: 'annual_leave',
                                                        disabled:
                                                            calDayOffLeftEmployee() <
                                                            0,
                                                    },
                                                ]}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="grid lg:grid-cols-4 grid-cols-1">
                                    <Typogaphy
                                        size="small"
                                        className="col-span-1"
                                    >
                                        {translate('day_off_reason')}
                                        <span className="text-red-500">*</span>
                                    </Typogaphy>
                                    <div className="col-span-3 lg:mt-0 mt-2">
                                        <FormItem
                                            id={'day-off-reason'}
                                            control={control}
                                            name="day_off.day_off_reason"
                                            type="textarea"
                                            label={translate(
                                                'dayoff-reason-label'
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-6 grid-cols-1 gap-2">
                        <div className="lg:col-span-5 col-span-1 px-4 md:p-4 md:pt-0 md:pb-4 bg-white rounded-lg">
                            <Typogaphy size="medium" className="text-primary">
                                {translate('day_off_range_time')}:
                            </Typogaphy>
                            <div className="lg:col-span-4">
                                {watch('day_off.day_off_range_time')?.map(
                                    (item, index) => {
                                        return (
                                            <RangeTimeItem
                                                key={`${item?.start_day_off}-${index}`}
                                                className="grid lg:grid-cols-5 gap-2"
                                                position={index}
                                                setValue={setValue}
                                                watch={watch}
                                                dayOff={dayOff}
                                                pageType={pageType}
                                                removeRangeTime={
                                                    removeRangeTime
                                                }
                                                item={item}
                                                calDayOffLeftEmployee={
                                                    calDayOffLeftEmployee
                                                }
                                                rangeTimeRendered={
                                                    rangeTimeRendered
                                                }
                                                setRangeTimeRendered={
                                                    setRangeTimeRendered
                                                }
                                                minDate={minDate}
                                            />
                                        )
                                    }
                                )}
                                <div className="grid lg:grid-cols-5 grid-cols-1">
                                    <div className="lg:col-start-2 lg:col-span-4 col-span-12">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.preventDefault()
                                                addRangeTime()
                                            }}
                                            className="w-full bg-[#F9FBFF]"
                                        >
                                            <AddIcons />
                                        </IconButton>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-span-1 gap-2">
                            <div className="bg-white px-4 md:p-4 md:pt-4 md:pb-4 mt-2 rounded-lg">
                                <Typogaphy
                                    size="medium"
                                    className="text-primary"
                                >
                                    {translate('number_of_day_off')}
                                </Typogaphy>
                                <div className="bg-[#F5F9FF] rounded-lg px-4 py-3 mt-1 text-center lg:h-[80px] flex items-center justify-center">
                                    <Typogaphy className="text-red-500">
                                        {watch('day_off.day_off_range_time')
                                            .map((item) => {
                                                switch (item?.day_off_option) {
                                                    case 0:
                                                    case 1:
                                                        return 0.5
                                                    case 2:
                                                        return 1
                                                    default:
                                                        break
                                                }
                                            })
                                            .reduce((a, b) => a + (b || 0), 0)}
                                    </Typogaphy>
                                </div>
                            </div>
                            <div className="bg-white px-4 rounded-lg">
                                <Typogaphy
                                    size="medium"
                                    className="text-primary"
                                >
                                    {translate('day_off_left')}
                                </Typogaphy>
                                <div className="bg-[#F6F6F6] rounded-lg px-4 py-3 mt-1 text-center lg:h-[80px] flex items-center justify-center">
                                    <Typogaphy
                                        className="text-red-500"
                                        id="employee-day-off-left"
                                    >
                                        {watch('day_off.day_off_type') ===
                                        'annual_leave'
                                            ? Number(
                                                  employee?.employee_day_off_left
                                              ) -
                                              (watch(
                                                  'day_off.day_off_range_time'
                                              )
                                                  .map((item) => {
                                                      switch (
                                                          item?.day_off_option
                                                      ) {
                                                          case 0:
                                                          case 1:
                                                              return 0.5
                                                          case 2:
                                                              return 1
                                                          default:
                                                              break
                                                      }
                                                  })
                                                  .reduce(
                                                      (a, b) => a + (b || 0),
                                                      0
                                                  ) -
                                                  (dayOff?.day_off_id
                                                      ? dayOff?.day_off_range_time
                                                            ?.map((item) => {
                                                                switch (
                                                                    item?.day_off_option
                                                                ) {
                                                                    case 0:
                                                                    case 1:
                                                                        return 0.5
                                                                    case 2:
                                                                        return 1
                                                                    default:
                                                                        break
                                                                }
                                                            })
                                                            .reduce(
                                                                (a, b) =>
                                                                    a +
                                                                    (b || 0),
                                                                0
                                                            )
                                                      : 0))
                                            : employee?.employee_day_off_left}
                                    </Typogaphy>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="grid lg:grid-cols-6 grid-cols-1">
                        <div className="col-span-2 col-start-4">
                            <div className="mb-4 lg:mt-0 mt-4 lg:mr-0 mr-4 flex items-center justify-end gap-4 ">
                                <Link to="/account/dayoffs">
                                    <Button variant="outlined" size="small">
                                        {translate('cancle-operation')}
                                    </Button>
                                </Link>
                                <Button
                                    loading={isCreating || isUpdating}
                                    variant="contained"
                                    disabled={
                                        !isValid ||
                                        isCreating ||
                                        isUpdating ||
                                        !dateValidation.isValid ||
                                        !leaveReservationValidation.isValid ||
                                        (pageType !== 'create' &&
                                            dayOff?.day_off_status !==
                                                'pending')
                                    }
                                    onClick={handleSubmit(onSubmit)}
                                >
                                    {translate(
                                        pageType === 'create'
                                            ? 'create'
                                            : 'update'
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export const DayOffCreatePage = (props: DayOffFormProps): React.ReactNode => {
    return <DayOffCreate {...props} />
}
